package com.bulut.mecitogullari.data.model

import com.bulut.mecitogullari.core.util.YearMonth
import kotlinx.serialization.Serializable

@Serializable
enum class TransactionType { INCOME, EXPENSE }

/**
 * A single income or expense entry. Everything is cash.
 *
 * Each entry belongs to a calendar month (derived from [dateEpochMillis])
 * and can optionally be tied to a construction project, so it shows up
 * both in the monthly ledger and on the project's own page.
 */
@Serializable
data class CashTransaction(
    val id: String,
    val type: TransactionType,
    val amount: Double,
    val description: String = "",
    /** Income type (e.g. "Daire Satışı") or expense category (e.g. "Malzeme"). */
    val category: String = "",
    /** Optional link to a construction project. */
    val projectId: String? = null,
    /**
     * false = project-only entry: it counts towards the project's totals but
     * is kept out of the monthly cash ledger (Panel / İşlemler / Rapor and
     * their exports). Used for spending that never passes through the till,
     * e.g. a card payment made straight to a supplier.
     */
    val inDailyLedger: Boolean = true,
    /** UTC-midnight epoch millis of the calendar day. */
    @Serializable(with = LongAsDoubleSerializer::class)
    val dateEpochMillis: Long,
)

/** Predefined Turkish category lists. */
object Categories {
    /** Selling an apartment — links the transaction to a specific unit. */
    const val APARTMENT_SALE = "Daire Satışı"

    val income = listOf(
        APARTMENT_SALE,
        "Kira Geliri",
        "Devir (Açılış Bakiyesi)",
        "Diğer Gelir",
    )
    val expense = listOf(
        "Malzeme",
        "İşçilik",
        "Taşeron",
        "Arsa / Tapu",
        "Vergi & Harç",
        "Nakliye & Yakıt",
        "Ofis / Genel",
        "Ev Gideri",
        "Diğer Gider",
    )
}

/** The monthly ledger summary the whole app revolves around. */
data class MonthSummary(
    val month: YearMonth,
    /** Net balance carried over from all previous months. */
    val openingBalance: Double = 0.0,
    val income: Double = 0.0,
    val expense: Double = 0.0,
) {
    val netProfit: Double get() = income - expense

    /** Carried forward to the next month. */
    val closingBalance: Double get() = openingBalance + netProfit
}

fun List<CashTransaction>.inMonth(month: YearMonth): List<CashTransaction> =
    filter { it.dateEpochMillis in month }

/** Entries the monthly cash ledger is built from — see [CashTransaction.inDailyLedger]. */
fun List<CashTransaction>.ledgerOnly(): List<CashTransaction> = filter { it.inDailyLedger }

fun computeMonthSummary(all: List<CashTransaction>, month: YearMonth): MonthSummary {
    val monthStart = month.startEpochMillis()
    var opening = 0.0
    var income = 0.0
    var expense = 0.0
    for (tx in all) {
        if (!tx.inDailyLedger) continue
        when {
            tx.dateEpochMillis < monthStart ->
                opening += if (tx.type == TransactionType.INCOME) tx.amount else -tx.amount
            tx.dateEpochMillis in month ->
                if (tx.type == TransactionType.INCOME) income += tx.amount else expense += tx.amount
        }
    }
    return MonthSummary(month, opening, income, expense)
}

/** Totals per category, largest first — used by the reports breakdown chart. */
fun List<CashTransaction>.categoryBreakdown(type: TransactionType): List<Pair<String, Double>> =
    filter { it.type == type }
        .groupBy { it.category.ifBlank { "Diğer" } }
        .map { (category, txs) -> category to txs.sumOf { it.amount } }
        .sortedByDescending { it.second }
